import { ConfigProvider } from 'antd';
import { useSession } from 'next-auth/react';
import { useSyncLanguage } from 'ni18n';
import { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { setToken } from '../../apis/grispiApi';
import useUserLanguage from '../../hooks/useUserLanguage';
import GrispiLoadingLayout from '../../layouts/GrispiLoadingLayout';
import { Languages } from '../../model/types';
import getAntdLocaleConfig from '../../utils/getAntdLocaleConfig';
import { changeLanguageOnLocalStorage, getDefaultLanguage } from '../../utils/i18n';

const LanguageContext = createContext<Languages>(Languages.EN);

/**
 * It takes the language from the user's session
 *      and if it's not available, it takes the language from the local storage
 *      and if it's not available, it takes the language from the browser's language
 */
export default function LanguageProvider({ children }: PropsWithChildren) {
    const sessionRequirementForUserLanguage = useRef(true);
    const { data: session, status } = useSession();

    // HACK: this is a workaround to set token in useUserLanguage hook before AuthenticationGate component set the token, when there is already a session during the user's first visit of the app
    if (sessionRequirementForUserLanguage.current && status === 'authenticated' && session?.accessToken) {
        sessionRequirementForUserLanguage.current = false;
        setToken(session.accessToken);
    }
    const { data: userLanguage } = useUserLanguage();
    console.debug(`LanguageProvider , userLanguage:`, userLanguage);
    const { ready } = useTranslation();

    const clientSideLanguage = userLanguage ?? getDefaultLanguage();

    useSyncLanguage(clientSideLanguage); // change i18n language via library
    useEffect(() => {
        changeLanguageOnLocalStorage(clientSideLanguage);
    }, [clientSideLanguage]);

    return (
        <LanguageContext.Provider value={clientSideLanguage}>
            <ConfigProvider locale={getAntdLocaleConfig(clientSideLanguage)}>
                {/**
                 * We need to render a loading content, till the translation is loaded. Otherwise, it will cause a hydration-error
                 */}
                {ready ? children : <GrispiLoadingLayout />}
            </ConfigProvider>
        </LanguageContext.Provider>
    );
}

const useGrispiLanguage = () => useContext(LanguageContext);

export { useGrispiLanguage };
